import React from 'react';
import {
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Avatar,
} from '@mui/material';
import {
    TrendingUp,
    TrendingDown,
    Speed,
    Room,
    CalendarToday,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { MEASURE_CONFIG, MEASURE_TYPES, getMeasureIcon, getMeasureLabel, getMeasureUnit } from '../../utils/measurementConfig';

function MonthlyKpiCards({ stats, activeMeasure, onSelectMeasure }) {
    const { t } = useTranslation();

    if (!stats || !stats.statsByMeasure) return null;

    // Single measure selected view
    if (activeMeasure !== 'ALL') {
        const metricStat = stats.statsByMeasure[activeMeasure];
        const config = MEASURE_CONFIG[activeMeasure] || {};

        if (!metricStat) {
            return null;
        }

        const decimals = config.decimals ?? 1;

        return (
            <Grid container spacing={2.5} sx={{ mb: 3 }}>
                {/* Maximum Card */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        elevation={2}
                        sx={{
                            height: '100%',
                            borderLeft: `4px solid ${config.color || '#ff7300'}`,
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                    {t('common.maximum', 'Maximum')}
                                </Typography>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(239, 68, 68, 0.12)',
                                        color: '#ef4444',
                                        width: 32,
                                        height: 32,
                                    }}
                                >
                                    <TrendingUp fontSize="small" />
                                </Avatar>
                            </Box>

                            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: config.color }}>
                                {metricStat.max.toFixed(decimals)} <Typography component="span" variant="h6" color="text.secondary">{metricStat.unit}</Typography>
                            </Typography>

                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Room sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {metricStat.maxDetails.zone}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {metricStat.maxDetails.monthName}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Minimum Card */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        elevation={2}
                        sx={{
                            height: '100%',
                            borderLeft: '4px solid #3b82f6',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                    {t('common.minimum', 'Minimum')}
                                </Typography>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(59, 130, 246, 0.12)',
                                        color: '#3b82f6',
                                        width: 32,
                                        height: 32,
                                    }}
                                >
                                    <TrendingDown fontSize="small" />
                                </Avatar>
                            </Box>

                            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: '#3b82f6' }}>
                                {metricStat.min.toFixed(decimals)} <Typography component="span" variant="h6" color="text.secondary">{metricStat.unit}</Typography>
                            </Typography>

                            <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Room sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary" noWrap>
                                        {metricStat.minDetails.zone}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <CalendarToday sx={{ fontSize: 14, color: 'text.secondary' }} />
                                    <Typography variant="caption" color="text.secondary">
                                        {metricStat.minDetails.monthName}
                                    </Typography>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Overall Average Card */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        elevation={2}
                        sx={{
                            height: '100%',
                            borderLeft: '4px solid #10b981',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                    {t('common.overallAverage', 'Overall Average')}
                                </Typography>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(16, 185, 129, 0.12)',
                                        color: '#10b981',
                                        width: 32,
                                        height: 32,
                                    }}
                                >
                                    <Speed fontSize="small" />
                                </Avatar>
                            </Box>

                            <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: '#10b981' }}>
                                {metricStat.avg.toFixed(decimals)} <Typography component="span" variant="h6" color="text.secondary">{metricStat.unit}</Typography>
                            </Typography>

                            <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                    label={`${metricStat.count} ${t('common.recordsCount', { count: metricStat.count })}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>

                {/* Active Zones & Total Data Points Card */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                    <Card
                        elevation={2}
                        sx={{
                            height: '100%',
                            borderLeft: '4px solid #8b5cf6',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': { transform: 'translateY(-2px)', boxShadow: 4 },
                        }}
                    >
                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, letterSpacing: 0.8 }}>
                                    {t('common.activeZones', 'Active Zones')}
                                </Typography>
                                <Avatar
                                    sx={{
                                        bgcolor: 'rgba(139, 92, 246, 0.12)',
                                        color: '#8b5cf6',
                                        width: 32,
                                        height: 32,
                                    }}
                                >
                                    <Room fontSize="small" />
                                </Avatar>
                            </Box>

                            <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
                                {stats.uniqueZones}
                            </Typography>

                            <Box sx={{ mt: 1.5 }}>
                                <Typography variant="caption" color="text.secondary">
                                    {stats.totalRecords} {t('common.totalRecords', 'Total Records')}
                                </Typography>
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        );
    }

    // ALL measures view: 4 metric summary cards
    return (
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {MEASURE_TYPES.map((type) => {
                const metricStat = stats.statsByMeasure[type];
                const config = MEASURE_CONFIG[type] || {};
                const decimals = config.decimals ?? 1;

                if (!metricStat) {
                    return (
                        <Grid size={{ xs: 12, sm: 6, md: 3 }} key={type}>
                            <Card elevation={2} sx={{ height: '100%', borderLeft: `4px solid ${config.color || '#999'}`, opacity: 0.6 }}>
                                <CardContent sx={{ p: 2.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {getMeasureIcon(type, { fontSize: 20 })}
                                        <Typography variant="subtitle2" color="text.secondary">
                                            {getMeasureLabel(type, t)}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body2" color="text.secondary">
                                        {t('common.noData', 'No data')}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    );
                }

                return (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={type}>
                        <Card
                            elevation={2}
                            onClick={() => onSelectMeasure && onSelectMeasure(type)}
                            sx={{
                                height: '100%',
                                borderLeft: `4px solid ${config.color}`,
                                cursor: 'pointer',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': { transform: 'translateY(-2px)', boxShadow: 5 },
                            }}
                        >
                            <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getMeasureIcon(type, { fontSize: 22 })}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {getMeasureLabel(type, t)}
                                        </Typography>
                                    </Box>
                                    <Chip
                                        label={getMeasureUnit(type)}
                                        size="small"
                                        sx={{
                                            height: 20,
                                            fontSize: '0.75rem',
                                            bgcolor: config.lightBg,
                                            color: config.color,
                                            fontWeight: 700,
                                        }}
                                    />
                                </Box>

                                <Box sx={{ mb: 1.5 }}>
                                    <Typography variant="caption" color="text.secondary" display="block">
                                        {t('common.overallAverage', 'Average')}
                                    </Typography>
                                    <Typography variant="h4" component="div" sx={{ fontWeight: 700, color: config.color }}>
                                        {metricStat.avg.toFixed(decimals)} <Typography component="span" variant="body1" color="text.secondary">{metricStat.unit}</Typography>
                                    </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
                                    <Box>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {t('common.minimum', 'Min')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#3b82f6' }}>
                                            {metricStat.min.toFixed(decimals)} {metricStat.unit}
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'right' }}>
                                        <Typography variant="caption" color="text.secondary" display="block">
                                            {t('common.maximum', 'Max')}
                                        </Typography>
                                        <Typography variant="body2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                                            {metricStat.max.toFixed(decimals)} {metricStat.unit}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                );
            })}
        </Grid>
    );
}

export default MonthlyKpiCards;
